import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { EnvelopeOpen, PaperPlaneTilt, BellSimple, BellSimpleSlash } from "@phosphor-icons/react";

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtCountdown = (s) => {
  if (typeof s !== "number" || s <= 0) return "soon";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 24) return `in ${Math.round(h / 24)}d ${h % 24}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
};

export default function ExpiryDigestCard({ onAction }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () =>
    api
      .get("/admin/expiry-digest/status")
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const sendNow = async () => {
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/admin/expiry-digest/run");
      if (data.sent) {
        setMsg(`Sent — ${data.soon} expiring soon, ${data.expired} expired.`);
      } else if (data.skipped) {
        setMsg(`Skipped (${data.skipped}).`);
      } else if (data.reason === "smtp-failed") {
        setMsg("SMTP failed — check logs.");
      } else {
        setMsg("No email sent.");
      }
      load();
      onAction?.();
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const toggleEnabled = async () => {
    if (toggleBusy || !status) return;
    setToggleBusy(true);
    try {
      await api.put(`/admin/expiry-digest/config?enabled=${!status.enabled}`);
      await load();
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setToggleBusy(false);
    }
  };

  const showPreview = async () => {
    setPreviewOpen(true);
    if (preview) return;
    try {
      const { data } = await api.get("/admin/expiry-digest/preview");
      setPreview(data);
    } catch (e) {
      setPreview({ subject: "Could not load preview", body: formatApiError(e) });
    }
  };

  if (loading || !status) return null;

  const enabled = status.enabled !== false;
  const soonCount = status.today_soon_count || 0;
  const expiredCount = status.today_expired_count || 0;
  const lastSent = fmtTime(status.last_sent_at);

  return (
    <>
      <div
        className="rounded-[24px] border border-[#2c3854] bg-[#141e32] p-6 mb-6 flex flex-wrap items-center gap-5"
        data-testid="expiry-digest-card"
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: enabled ? "rgba(221, 166, 50, 0.15)" : "rgba(168, 178, 204, 0.1)" }}
        >
          <EnvelopeOpen
            size={22}
            weight="duotone"
            color={enabled ? "#dda632" : "#a8b2cc"}
          />
        </div>

        <div className="flex-1 min-w-[220px]">
          <div className="font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-1">
            Daily expiry digest
          </div>
          <div className="font-display text-lg tracking-tight text-[#f5ecd9]">
            {soonCount === 0 && expiredCount === 0
              ? "Nothing to flag today"
              : `${soonCount} expiring soon · ${expiredCount} just ended`}
          </div>
          <div className="text-[0.78rem] text-[#a8b2cc] mt-0.5">
            {enabled ? (
              <>
                Auto-send next run {fmtCountdown(status.next_run_in_seconds)} (07:00 SAST).
                {lastSent ? ` Last sent ${lastSent}.` : " Not yet sent."}
              </>
            ) : (
              <>Auto-send is paused.{lastSent ? ` Last sent ${lastSent}.` : ""}</>
            )}
          </div>
          {msg && (
            <div className="text-[0.78rem] mt-2 text-[#dda632]" data-testid="digest-msg">
              {msg}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={showPreview}
            className="text-[0.7rem] uppercase tracking-[0.18em] font-nav font-semibold text-[#a8b2cc] hover:text-[#f5ecd9] px-3 py-2"
            data-testid="digest-preview-btn"
          >
            Preview
          </button>
          <button
            onClick={toggleEnabled}
            disabled={toggleBusy}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-[0.7rem] uppercase tracking-[0.18em] font-nav font-semibold transition-colors disabled:opacity-60 ${
              enabled
                ? "border-[#2c3854] text-[#a8b2cc] hover:border-[#af3fa6] hover:text-[#af3fa6]"
                : "border-[#dda632] text-[#dda632] hover:bg-[#dda632] hover:text-[#070b14]"
            }`}
            data-testid="digest-toggle-btn"
            title={enabled ? "Pause auto-send" : "Resume auto-send"}
          >
            {enabled ? (
              <>
                <BellSimpleSlash size={14} weight="bold" /> Pause
              </>
            ) : (
              <>
                <BellSimple size={14} weight="bold" /> Resume
              </>
            )}
          </button>
          <button
            onClick={sendNow}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#dda632] text-[#070b14] text-[0.7rem] uppercase tracking-[0.18em] font-nav font-semibold hover:bg-[#f5ecd9] transition-colors disabled:opacity-60"
            data-testid="digest-send-btn"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {busy ? "Sending…" : "Send now"}
          </button>
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-[#141e32] border border-[#2c3854] rounded-[24px] w-full max-w-2xl max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="digest-preview-modal"
          >
            <div className="p-6 border-b border-[#2c3854]">
              <div className="overline text-[#a8b2cc] mb-1">Email preview</div>
              <h3 className="font-display text-2xl tracking-tighter text-[#f5ecd9]">
                {preview?.subject || "Loading…"}
              </h3>
            </div>
            <pre className="p-6 text-sm text-[#a8b2cc] whitespace-pre-wrap font-mono-accent leading-relaxed">
              {preview?.body || "Loading preview…"}
            </pre>
            <div className="p-6 border-t border-[#2c3854] flex justify-end">
              <button
                onClick={() => setPreviewOpen(false)}
                className="btn-ghost"
                data-testid="digest-preview-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
